'use client';

import { PROCEDURE_TYPE_PARAM_KEY } from '@/components/custom-layout/customer-v2/type';
import { useURLState } from '@/hooks/use-url-state';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import {
    ProcedureTypeSelectionMode,
    resolvePTypeAutoRedirect,
    resolveUserRole,
    treeHasStaffCategoryNode,
    withStaffCategory,
} from '@/server/api/v1/procedure-type/procedure-type-eligibility';
import { useMemo } from 'react';
import { useQueryShowUiProcedureTypeTree } from './use-query-procedure-type-tree';
import { useQueryThongTinGiangVien } from './use-query-thong-tin-giang-vien';

/**
 * Chế độ hiển thị/lọc cây loại thủ tục — đọc cùng env với
 * `sub-header-nav-bar.tsx` để gate query KHỚP với logic auto-redirect ở đó.
 */
const PROCEDURE_TYPE_MODE: ProcedureTypeSelectionMode =
    process.env.NEXT_PUBLIC_PROCEDURE_TYPE_MODE === 'personalized'
        ? 'personalized'
        : 'public';

/**
 * Cho biết lựa chọn `p-types` trên URL đã "ổn định" (settled) chưa — tức
 * vùng "Danh sách thủ tục" (sub-header) đã load xong cây VÀ đã áp xong
 * p-types mặc định (nếu có) vào URL.
 *
 * Mục đích: gate query bảng thủ tục để bảng KHÔNG fetch "tất cả" ở lần paint
 * đầu (khi URL chưa có p-types) rồi lại fetch lần 2 sau khi default được áp
 * → tránh nhấp nháy / dữ liệu sai thoáng qua.
 *
 * Dùng chung `resolvePTypeAutoRedirect` với sub-header nên điều kiện chờ ở
 * đây luôn khớp với điều kiện redirect bên đó.
 *
 * Lưu ý: hook truyền `userInteracted: false` vì nó chỉ phục vụ gate LẦN TẢI
 * ĐẦU; consumer (ProcedureCust) latch lại `isReady` nên các thao tác chọn
 * p-type sau đó của user không làm bảng bị disable trở lại.
 */
export const useProcedureSelectionReady = (): {
    /** p-types đã ổn định, an toàn để fetch dữ liệu phụ thuộc p-types. */
    isReady: boolean;
    /** Query cây loại thủ tục đang tải lần đầu. */
    isLoadingTree: boolean;
} => {
    const { data: rawProcedureTypes, isLoading: isLoadingTree } =
        useQueryShowUiProcedureTypeTree();
    const authData = useAuthenticationStore((x) => x.auth);
    const [currentPType] = useURLState(PROCEDURE_TYPE_PARAM_KEY);

    const userRole = useMemo(() => resolveUserRole(authData), [authData]);

    // Phân loại cán bộ (HRM `MaPhanLoai`) — cần cho lọc node `employee-*`. Bật
    // hook cùng điều kiện với `sub-header-nav-bar.tsx` để 2 bên đồng bộ.
    const needStaffCategory =
        PROCEDURE_TYPE_MODE === 'personalized' &&
        userRole?.role === 'Staff' &&
        treeHasStaffCategoryNode(rawProcedureTypes);
    const { data: thongTinGiangVien, isReady: isStaffCategoryReady } =
        useQueryThongTinGiangVien(
            userRole?.identityCode ?? '',
            needStaffCategory
        );
    const effectiveUserRole = useMemo(
        () => withStaffCategory(userRole, thongTinGiangVien),
        [userRole, thongTinGiangVien]
    );

    const isTreeResolving =
        isLoadingTree || (needStaffCategory && !isStaffCategoryReady);

    const isReady = useMemo(() => {
        if (isTreeResolving) return false;
        const { shouldRedirect } = resolvePTypeAutoRedirect({
            rawProcedureTypes,
            isTreeLoading: isTreeResolving,
            userRole: effectiveUserRole,
            currentPType,
            mode: PROCEDURE_TYPE_MODE,
            userInteracted: false,
        });
        // shouldRedirect = true nghĩa là p-types còn sắp đổi → CHƯA sẵn sàng.
        return !shouldRedirect;
    }, [rawProcedureTypes, isTreeResolving, effectiveUserRole, currentPType]);

    return { isReady, isLoadingTree };
};
